import type { CampaignDesiredStateDto } from 'src/store/generated/leela-api-generated';
import { groupStrategiesByAbTesting } from 'src/utils/abTestingUtils';
import type { ClonedCampaignData, ClonedProductStrategy } from 'src/types/form.types';

/**
 * Builds a partial campaign object suitable for pre-filling the create modal
 * when cloning an existing campaign. Strips identity/stale fields and clears
 * fields that require new user input.
 *
 * For A/B testing groups (multiple strategies sharing a groupId), collapses
 * them back to a single representative strategy with abTestingEnabled and
 * the correct variation count, so the form toggle is properly initialized.
 */
export function buildClonedCampaignData (
  source: CampaignDesiredStateDto
): ClonedCampaignData {
  const {
    campaignId,
    createdAt,
    updatedAt,
    adSets,
    name,
    programId,
    publisherAccountConfig,
    publisherCampaignConfig,
    configIds,
    ...rest
  } = source;

  // Detect A/B testing groups and collapse them to single representative strategies
  const { abTestingGroups, ungroupedStrategies } = groupStrategiesByAbTesting(rest.productStrategies);

  const clonedStrategies: ClonedProductStrategy[] = [];

  // Collapse each AB group to a single representative with form-level AB fields
  for (const group of abTestingGroups) {
    const { productSetId, groupId, customCatalogConfig, ...representative } = group.strategies[0].strategy;
    clonedStrategies.push({
      ...representative,
      productSetId: undefined,
      groupId: undefined,
      customCatalogConfig: undefined,
      abTestingEnabled: true,
      abTestingVariations: group.strategies.length,
      autoGenerateProductSet: true,
    });
  }

  // Ungrouped strategies get autoGenerateProductSet so the user can auto-generate
  for (const { strategy } of ungroupedStrategies) {
    const { productSetId, groupId, customCatalogConfig, ...clean } = strategy;
    clonedStrategies.push({
      ...clean,
      productSetId: undefined,
      groupId: undefined,
      customCatalogConfig: undefined,
      autoGenerateProductSet: true,
    });
  }

  return {
    ...rest,
    name: undefined,
    productStrategies: clonedStrategies,
    campaignCountry: source.productStrategies[0]?.country ?? '',
    campaignBusinessLine: source.productStrategies[0]?.businessLine ?? '',
  };
}
